import React, {useMemo} from 'react';
import {injectIntl} from 'react-intl';
import {Scrollbars} from 'react-custom-scrollbars';

import Util from '../../../../../forecast-app/shared/util/util';
import BudgetTable from './budget_table/budget_table';
import GenericEmptyState from '../../../../empty-state/generic_empty_state_v2';
import {TABLE_TABS} from '../../../../../constants';
import {MODAL_TYPE, showModal} from '../../../../../forecast-app/shared/components/modals/generic_modal_conductor';
import * as tracking from '../../../../../tracking';
import {Button} from 'web-components';
import styled from 'styled-components';
import {calculateOptions, getNextAvailableLock} from './budget_table/period_locks';
import ForecastTooltip from '../../../../../forecast-app/shared/components/tooltips/ForecastTooltip';

export const ActualsHeaderContainer = styled.div`
	display: flex;
	flex-grow: 1;
`;

export const LockButtonContainer = styled.div`
	display: flex;
	flex-grow: 1;
	justify-content: flex-end;
	Button {
		margin-left: 7px;
	}
`;

const StickyContainer = styled.span`
	display: flex;
	position: sticky;
	right: 29px;
`;

const TablesSection = ({
	intl: {formatMessage},
	retry,
	isFixedPriceV2,
	marginTop,
	isMoneyTabSelected,
	tableContainerRef,
	tablesSectionRef,
	areTablesOutOfScreen,
	tablesData,
	formatTableData,
	theEyeOptions,
	tooltipsText: {
		actualsTooltipText,
		planTooltipText,
		actualVarianceToPlanTooltipText,
		remainingTooltipText,
		forecastTooltipText,
		forecastVarianceToPlanTooltipText,
		invoiceVarianceToActualTooltipText,
		baselineTooltipText,
		baselineVarianceToActualsTooltipText,
		baselineVarianceToForecastTooltipText,
	},
	selectedTablesTab,
	handleTablesTabSelected,
	missingProjectDates,
	addProjectDates,
	project,
	todayPeriodIndex,
	lockedColumnIndexMap,
	disabledColumnIndexes,
	isTableDataLoading,
	currencySymbol,
	setShowFixedPriceUpdateToast,
}) => {
	const tableVariancesFilterOptions = Util.getNestedOptionsObject(theEyeOptions, 'tableVariances');
	const tablesFilterOptions = Util.getNestedOptionsObject(theEyeOptions, 'tables');
	const tableDetailsFilterOptions = Util.getNestedOptionsObject(theEyeOptions, 'tableDetails');
	const tableNumbersFilterOptions = Util.getNestedOptionsObject(theEyeOptions, 'tableNumbers');
	const canExpand = !!(tableDetailsFilterOptions.tableDetails || tableNumbersFilterOptions.accumulatedTotals);
	const nextAvailableLock = getNextAvailableLock(project);
	let disableLockButton = false;
	if (nextAvailableLock) {
		const possibleLocks = calculateOptions(project, nextAvailableLock);
		disableLockButton = possibleLocks.length === 0 || project.status === 'DONE';
	}

	const showFixedPriceLockModal = () => {
		showModal({
			type: MODAL_TYPE.FIXED_PRICE_LOCK,
			project,
			retry,
		});

		tracking.trackElementClicked('Fixed Price Lock button');
	};

	const showFixedPriceOpenLockModal = () => {
		showModal({
			type: MODAL_TYPE.FIXED_PRICE_OPEN_LOCK,
			project,
			callback: function () {
				setShowFixedPriceUpdateToast(true);
			},
		});

		tracking.trackElementClicked('Fixed Price Open Lock button');
	};

	// custom horizontal scrollbar style
	const renderTrackHorizontal = () => {
		const finalStyle = {
			position: 'fixed', //make the scrollabar available at the bottom on the portview
			height: 6,
			right: 66,
			bottom: 2,
			left: 66,
			borderRadius: 3,
		};
		return <div style={finalStyle} />;
	};

	return (
		<div className="tables-section" style={{marginTop}} ref={tablesSectionRef}>
			<div className="section-title">{formatMessage({id: 'common.tables'})}</div>
			{missingProjectDates ? (
				<div className="tables-wrapper empty">
					<GenericEmptyState header={formatMessage({id: 'project_budget.missing_project_dates_info'})} callback={addProjectDates} actionText={formatMessage({id: 'project_budget.add_dates'})} />
				</div>
			) : (
				<>
					<div className="tables-tabs" data-userpilot="budget-table-tabs">
						{isMoneyTabSelected ? (
							<div className={`tab ${selectedTablesTab === TABLE_TABS.WORK_AND_EXPENSES ? 'selected' : ''}`} onClick={() => handleTablesTabSelected(TABLE_TABS.WORK_AND_EXPENSES)}>
								{formatMessage({id: 'project_budget.total_price_of_work_and_expenses'})}
							</div>
						) : null}
						<div
							// select work tab by default when time unit is selected
							className={`tab ${
								selectedTablesTab === TABLE_TABS.WORK || (!isMoneyTabSelected && (selectedTablesTab === TABLE_TABS.WORK_AND_EXPENSES || selectedTablesTab === TABLE_TABS.EXPENSES || selectedTablesTab === TABLE_TABS.INVOICE))
									? 'selected'
									: ''
							}`}
							onClick={() => handleTablesTabSelected(TABLE_TABS.WORK)}
						>
							{formatMessage({id: 'project_budget.work_details'})}
						</div>
						{isMoneyTabSelected ? (
							<div className={`tab ${selectedTablesTab === TABLE_TABS.EXPENSES ? 'selected' : ''}`} onClick={() => handleTablesTabSelected(TABLE_TABS.EXPENSES)}>
								{formatMessage({id: 'project_budget.expense_details'})}
							</div>
						) : null}
						{isMoneyTabSelected && project.billable ? (
							<div className={`tab ${selectedTablesTab === TABLE_TABS.INVOICE ? 'selected' : ''}`} onClick={() => handleTablesTabSelected(TABLE_TABS.INVOICE)}>
								{formatMessage({id: 'project_budget.invoice_plan'})}
							</div>
						) : null}
					</div>
					<div className="table-scroll-container" data-userpilot="budget-tables">
						<Scrollbars autoHeight autoHeightMax={900000} renderTrackHorizontal={() => renderTrackHorizontal()}>
							<div className={`tables-wrapper`}>
								{isTableDataLoading ? <div className="loader"></div> : null}
								{useMemo(
									() =>
										!isTableDataLoading && tablesFilterOptions.baseline && tablesData.baselineTableData ? (
											<div ref={tableContainerRef} className={`table-container ${areTablesOutOfScreen ? 'out-of-screen' : ''}`}>
												<div className="table-name">
													<span>{formatMessage({id: 'baseline.baseline'})}</span>
												</div>
												<BudgetTable
													dataType={'baseline'}
													name="Baseline"
													data={formatTableData(tablesData.baselineTableData)}
													showMilestones={tableDetailsFilterOptions.milestone}
													showRoles={tableDetailsFilterOptions.role}
													color="#f5f5f8"
													highlightColumnIndex={todayPeriodIndex}
													lockedColumnIndexMap={lockedColumnIndexMap}
													disabledColumnIndexes={disabledColumnIndexes}
													withCollapseAll={selectedTablesTab === TABLE_TABS.WORK_AND_EXPENSES || selectedTablesTab === TABLE_TABS.WORK}
													canExpand={canExpand}
													rowTooltipText={baselineTooltipText}
													currencySymbol={currencySymbol}
												/>
											</div>
										) : null,
									[isTableDataLoading, tablesData.baselineTableData]
								)}

								{useMemo(
									() =>
										!isTableDataLoading && tableVariancesFilterOptions.baselineVarianceToActuals && tablesData.baselineVarianceToActualsTableData ? (
											<div ref={tableContainerRef} className={`table-container ${areTablesOutOfScreen ? 'out-of-screen' : ''}`}>
												<div className="table-name">
													<span>{formatMessage({id: 'project_budget.baseline_vs_actuals'})}</span>
												</div>
												<BudgetTable
													dataType={'baseline_vs_actuals'}
													name="Baseline vs. Actuals"
													data={formatTableData(tablesData.baselineVarianceToActualsTableData)}
													showMilestones={tableDetailsFilterOptions.milestone}
													showRoles={tableDetailsFilterOptions.role}
													color="#f5f5f8"
													highlightColumnIndex={todayPeriodIndex}
													lockedColumnIndexMap={lockedColumnIndexMap}
													disabledColumnIndexes={disabledColumnIndexes}
													withCollapseAll={selectedTablesTab === TABLE_TABS.WORK_AND_EXPENSES || selectedTablesTab === TABLE_TABS.WORK}
													canExpand={canExpand}
													rowTooltipText={baselineVarianceToActualsTooltipText}
													currencySymbol={currencySymbol}
												/>
											</div>
										) : null,
									[isTableDataLoading, tablesData.baselineTableData]
								)}

								{useMemo(
									() =>
										!isTableDataLoading && tableVariancesFilterOptions.baselineVarianceToForecast && tablesData.baselineVarianceToForecastTableData ? (
											<div ref={tableContainerRef} className={`table-container ${areTablesOutOfScreen ? 'out-of-screen' : ''}`}>
												<div className="table-name">
													<span>{formatMessage({id: 'project_budget.baseline_vs_forecast'})}</span>
												</div>
												<BudgetTable
													dataType={'baseline_vs_forecast'}
													name="Baseline vs. Forecast"
													data={formatTableData(tablesData.baselineVarianceToForecastTableData)}
													showMilestones={tableDetailsFilterOptions.milestone}
													showRoles={tableDetailsFilterOptions.role}
													color="#f5f5f8"
													highlightColumnIndex={todayPeriodIndex}
													lockedColumnIndexMap={lockedColumnIndexMap}
													disabledColumnIndexes={disabledColumnIndexes}
													withCollapseAll={selectedTablesTab === TABLE_TABS.WORK_AND_EXPENSES || selectedTablesTab === TABLE_TABS.WORK}
													canExpand={canExpand}
													rowTooltipText={baselineVarianceToForecastTooltipText}
													currencySymbol={currencySymbol}
												/>
											</div>
										) : null,
									[isTableDataLoading, tablesData.baselineTableData]
								)}

								{useMemo(
									() =>
										!isTableDataLoading && tablesFilterOptions.actuals && tablesData.actualsTableData && !tablesData.invoiceVarianceActualTableData ? (
											<div ref={tableContainerRef} className={`table-container ${areTablesOutOfScreen ? 'out-of-screen' : ''}`}>
												<div className="table-name">
													<ActualsHeaderContainer>
														<span>{formatMessage({id: 'project_budget.actuals'})}</span>

														{isFixedPriceV2 ? (
															<LockButtonContainer>
																<StickyContainer>
																	<ForecastTooltip disabled={!disableLockButton} content={formatMessage({id: 'fixed_price_lock.tooltip.no_months_to_close'})}>
																		<Button disabled={disableLockButton} size={Button.SIZE.SMALL} variant={Button.VARIANT.PURPLE_FILLED} onClick={showFixedPriceLockModal}>
																			{formatMessage({id: 'fixed_price_lock.close_modal_button'})}
																		</Button>
																	</ForecastTooltip>
																	{lockedColumnIndexMap.size ? (
																		<Button size={Button.SIZE.SMALL} variant={Button.VARIANT.PURPLE_OUTLINE} onClick={showFixedPriceOpenLockModal}>
																			{formatMessage({id: 'fixed_price_lock.open_modal_button'})}
																		</Button>
																	) : null}
																</StickyContainer>
															</LockButtonContainer>
														) : (
															<div />
														)}
													</ActualsHeaderContainer>
												</div>

												<BudgetTable
													dataType={'actuals'}
													name={formatMessage({id: 'project_budget.actuals'})}
													data={formatTableData(tablesData.actualsTableData)}
													showMilestones={tableDetailsFilterOptions.milestone}
													showRoles={tableDetailsFilterOptions.role}
													color="#f5f5f8"
													highlightColumnIndex={todayPeriodIndex}
													lockedColumnIndexMap={lockedColumnIndexMap}
													disabledColumnIndexes={disabledColumnIndexes}
													withCollapseAll={selectedTablesTab === TABLE_TABS.WORK_AND_EXPENSES || selectedTablesTab === TABLE_TABS.WORK}
													canExpand={canExpand}
													rowTooltipText={actualsTooltipText}
													currencySymbol={currencySymbol}
												/>
											</div>
										) : null,
									[isTableDataLoading, tablesData.actualsTableData, tablesData.invoiceVarianceActualTableData]
								)}

								{useMemo(
									() =>
										!isTableDataLoading && tablesFilterOptions.plan && tablesData.planTableData ? (
											<div className={`table-container ${areTablesOutOfScreen ? 'out-of-screen' : ''}`}>
												<div className="table-name">
													<span>{formatMessage({id: 'common.plan'})}</span>
												</div>

												<BudgetTable
													dataType={'plan'}
													name={formatMessage({id: 'common.plan'})}
													data={formatTableData(tablesData.planTableData)}
													showMilestones={tableDetailsFilterOptions.milestone}
													showRoles={tableDetailsFilterOptions.role}
													color="#f5f5f8"
													highlightColumnIndex={todayPeriodIndex}
													lockedColumnIndexMap={lockedColumnIndexMap}
													disabledColumnIndexes={disabledColumnIndexes}
													withCollapseAll={selectedTablesTab === TABLE_TABS.WORK_AND_EXPENSES || selectedTablesTab === TABLE_TABS.WORK}
													canExpand={canExpand}
													rowTooltipText={planTooltipText}
													currencySymbol={currencySymbol}
												/>
											</div>
										) : null,
									[isTableDataLoading, tablesData.planTableData]
								)}

								{useMemo(
									() =>
										!isTableDataLoading && tablesData.actualsVariancePlanTableData && tableVariancesFilterOptions.actualVarianceToPlan ? (
											<div className={`table-container ${areTablesOutOfScreen ? 'out-of-screen' : ''}`}>
												<>
													<div className="table-name">
														<span>{formatMessage({id: 'project_budget.actual_vs_plan'})}</span>
													</div>
													<BudgetTable
														dataType={'actualsVPlan'}
														name={formatMessage({id: 'project_budget.actual_vs_plan'})}
														data={formatTableData(tablesData.actualsVariancePlanTableData)}
														showMilestones={tableDetailsFilterOptions.milestone}
														showRoles={tableDetailsFilterOptions.role}
														color="#f5f5f8"
														highlightColumnIndex={todayPeriodIndex}
														lockedColumnIndexMap={lockedColumnIndexMap}
														disabledColumnIndexes={disabledColumnIndexes}
														withCollapseAll={selectedTablesTab === TABLE_TABS.WORK_AND_EXPENSES || selectedTablesTab === TABLE_TABS.WORK}
														canExpand={canExpand}
														rowTooltipText={actualVarianceToPlanTooltipText}
														currencySymbol={currencySymbol}
													/>
												</>
											</div>
										) : null,
									[isTableDataLoading, tablesData.actualsVariancePlanTableData, tableVariancesFilterOptions.actualVarianceToPlan]
								)}

								{useMemo(
									() =>
										!isTableDataLoading && tablesFilterOptions.remaining && tablesData.remainingTableData ? (
											<div className={`table-container ${areTablesOutOfScreen ? 'out-of-screen' : ''}`}>
												<div className="table-name">
													<span>{formatMessage({id: 'cards.remaining'})}</span>
												</div>

												<BudgetTable
													dataType={'remaning'}
													name={formatMessage({id: 'cards.remaining'})}
													data={formatTableData(tablesData.remainingTableData)}
													showMilestones={tableDetailsFilterOptions.milestone}
													showRoles={tableDetailsFilterOptions.role}
													color={'#f5f5f8'}
													highlightColumnIndex={todayPeriodIndex}
													lockedColumnIndexMap={lockedColumnIndexMap}
													disabledColumnIndexes={disabledColumnIndexes}
													withCollapseAll={selectedTablesTab === TABLE_TABS.WORK_AND_EXPENSES || selectedTablesTab === TABLE_TABS.WORK}
													canExpand={canExpand}
													rowTooltipText={remainingTooltipText}
													currencySymbol={currencySymbol}
												/>
											</div>
										) : null,
									[isTableDataLoading, tablesData.remainingTableData]
								)}

								{useMemo(
									() =>
										!isTableDataLoading && tablesFilterOptions.forecast && tablesData.forecastTableData ? (
											<div className={`table-container ${areTablesOutOfScreen ? 'out-of-screen' : ''}`}>
												<div className="table-name">
													<span>{formatMessage({id: 'common.forecast'})}</span>
												</div>

												<BudgetTable
													dataType={'forecast'}
													name={formatMessage({id: 'common.forecast'})}
													data={formatTableData(tablesData.forecastTableData)}
													showMilestones={tableDetailsFilterOptions.milestone}
													showRoles={tableDetailsFilterOptions.role}
													color="#f5f5f8"
													highlightColumnIndex={todayPeriodIndex}
													lockedColumnIndexMap={lockedColumnIndexMap}
													disabledColumnIndexes={disabledColumnIndexes}
													withCollapseAll={selectedTablesTab === TABLE_TABS.WORK_AND_EXPENSES || selectedTablesTab === TABLE_TABS.WORK}
													canExpand={canExpand}
													rowTooltipText={forecastTooltipText}
													currencySymbol={currencySymbol}
												/>
											</div>
										) : null,
									[isTableDataLoading, tablesData.forecastTableData]
								)}

								{useMemo(
									() =>
										!isTableDataLoading && tablesData.forecastVariancePlanTableData && tableVariancesFilterOptions.forecastVarianceToPlan ? (
											<div className={`table-container ${areTablesOutOfScreen ? 'out-of-screen' : ''}`}>
												<>
													<div className="table-name">
														<span>{formatMessage({id: 'project_budget.forecast_vs_plan'})}</span>
													</div>
													<BudgetTable
														dataType={'forecastVPlan'}
														name={formatMessage({id: 'project_budget.forecast_vs_plan'})}
														data={formatTableData(tablesData.forecastVariancePlanTableData)}
														showMilestones={tableDetailsFilterOptions.milestone}
														showRoles={tableDetailsFilterOptions.role}
														color="#f5f5f8"
														highlightColumnIndex={todayPeriodIndex}
														lockedColumnIndexMap={lockedColumnIndexMap}
														disabledColumnIndexes={disabledColumnIndexes}
														withCollapseAll={selectedTablesTab === TABLE_TABS.WORK_AND_EXPENSES || selectedTablesTab === TABLE_TABS.WORK}
														canExpand={canExpand}
														rowTooltipText={forecastVarianceToPlanTooltipText}
														currencySymbol={currencySymbol}
													/>
												</>
											</div>
										) : null,
									[isTableDataLoading, tablesData.forecastVariancePlanTableData, tableVariancesFilterOptions.forecastVarianceToPlan]
								)}

								{useMemo(
									() =>
										!isTableDataLoading && tablesFilterOptions.invoice && tablesData.invoiceTableData ? (
											<div className={`table-container ${areTablesOutOfScreen ? 'out-of-screen' : ''}`}>
												<>
													<div className="table-name">
														<span>{formatMessage({id: 'project_section.invoice'})}</span>
													</div>
													<BudgetTable
														name={formatMessage({id: 'project_section.invoice'})}
														data={formatTableData(tablesData.invoiceTableData)}
														color="#f5f5f8"
														highlightColumnIndex={todayPeriodIndex}
														lockedColumnIndexMap={new Map()}
														disabledColumnIndexes={disabledColumnIndexes}
														currencySymbol={currencySymbol}
													/>
												</>
											</div>
										) : null,
									[isTableDataLoading, tablesData.invoiceTableData]
								)}

								{useMemo(
									() =>
										!isTableDataLoading && tablesData.invoiceVarianceActualTableData && tableVariancesFilterOptions.invoiceVarianceToActuals ? (
											<div className={`table-container ${areTablesOutOfScreen ? 'out-of-screen' : ''}`}>
												<>
													<div className="table-name">
														<span>{formatMessage({id: 'project_budget.invoice_vs_actual'})}</span>
													</div>
													<BudgetTable
														name={formatMessage({id: 'project_budget.invoice_vs_actual'})}
														data={formatTableData(tablesData.invoiceVarianceActualTableData)}
														color="#f5f5f8"
														highlightColumnIndex={todayPeriodIndex}
														lockedColumnIndexMap={new Map()}
														disabledColumnIndexes={disabledColumnIndexes}
														rowTooltipText={invoiceVarianceToActualTooltipText}
														currencySymbol={currencySymbol}
													/>
												</>
											</div>
										) : null,
									[isTableDataLoading, tablesData.invoiceVarianceActualTableData, tableVariancesFilterOptions.invoiceVarianceToActuals]
								)}
							</div>
						</Scrollbars>
					</div>
				</>
			)}
		</div>
	);
};

export default injectIntl(TablesSection);
